/**
 * Customer brand colors for the portal embed.
 *
 * The Canopy portal themes its vertical nav with the customer's brand
 * palette (`primarycolor` → `--branding-bg-color`, `navtextcolor` →
 * `--branding-text-color`). Sage runs cross-origin in the iframe, so the
 * portal forwards those colors in the `canopy.auth` postMessage; we apply
 * them here as CSS custom properties (`--embed-brand-bg` / `--embed-brand-fg`)
 * that the embed tab buttons consume.
 *
 * The foreground is chosen to GUARANTEE legibility: we honor the portal's
 * nav text color when it clears a contrast threshold against the brand
 * background, otherwise we fall back to black/white by luminance. This
 * keeps the buttons on-brand without ever producing unreadable text.
 */

/** Parse #rgb / #rrggbb / rgb(r,g,b) into [r,g,b] 0–255, or null. */
function parseColor(input: string | null | undefined): [number, number, number] | null {
  if (!input) return null;
  const s = input.trim();
  const hex = s.replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    const r = parseInt(hex[0]! + hex[0]!, 16);
    const g = parseInt(hex[1]! + hex[1]!, 16);
    const b = parseInt(hex[2]! + hex[2]!, 16);
    return [r, g, b];
  }
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  const m = s.match(/^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return null;
}

/** WCAG relative luminance (0 = black, 1 = white). */
function relLuminance([r, g, b]: [number, number, number]): number {
  const lin = (c: number): number => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two colors (1–21). */
function contrastRatio(a: [number, number, number], b: [number, number, number]): number {
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Pick a legible foreground for `bg`: prefer `preferred` when it clears a
 * 4:1 contrast ratio, else black or white whichever contrasts more.
 */
export function legibleForeground(bg: string, preferred?: string | null): string {
  const bgRgb = parseColor(bg);
  if (!bgRgb) return preferred?.trim() || '#ffffff';
  const pref = parseColor(preferred);
  if (pref && contrastRatio(bgRgb, pref) >= 4) return (preferred as string).trim();
  const white: [number, number, number] = [255, 255, 255];
  const black: [number, number, number] = [26, 26, 26];
  return contrastRatio(bgRgb, white) >= contrastRatio(bgRgb, black) ? '#ffffff' : '#1a1a1a';
}

/**
 * Apply the customer brand to the embed. Sets `--embed-brand-bg` and a
 * contrast-checked `--embed-brand-fg` on :root; clears them when no
 * usable brand color is supplied (falls back to Sage's accent in CSS).
 */
export function applyBrand(bgColor: string | null | undefined, preferredFg?: string | null): void {
  const root = document.documentElement;
  const bg = bgColor?.trim();
  if (!bg || !parseColor(bg)) {
    root.style.removeProperty('--embed-brand-bg');
    root.style.removeProperty('--embed-brand-fg');
    return;
  }
  root.style.setProperty('--embed-brand-bg', bg);
  root.style.setProperty('--embed-brand-fg', legibleForeground(bg, preferredFg));
}
